import React, { useMemo, useState } from 'react';
import type { SeedKey } from '../../hooks/useSeedEffect';
import { useTranslation } from 'react-i18next';
import SchemaForm from '../../schemaForm/SchemaForm';
import ActivityPicker from './ActivityPicker';
import '../../App.css';

export interface IGroupFormData {
  name: string;
  description: string;
  activityId: number | null;
}

interface IGroupFormProps {
  initialData?: {
    name: string;
    description: string;
    activityId?: number | null;
  };
  /** Record identity — seeds once per record instead of per object identity. */
  seedKey?: SeedKey;
  onSubmit: (data: IGroupFormData) => Promise<void>;
  loading: boolean;
  submitButtonText: string;
  /** If set, narrows the activity picker to these ids (scoped admin). */
  allowedActivityIds?: number[];
}

/**
 * Thin wrapper around the schema-driven SchemaForm for a group's name and
 * description. The activity tagging (ActivityPicker) is route-managed metadata
 * that isn't part of the group entity meta (the backend resolves/guards
 * `activityId` itself), so it stays as a non-entity field rendered inline
 * inside the form below description. The picker's scoped-admin "required"
 * validation is preserved here, before the SchemaForm submit fires.
 */
const GroupForm: React.FC<IGroupFormProps> = ({
  initialData,
  seedKey,
  onSubmit,
  loading,
  submitButtonText,
  allowedActivityIds,
}) => {
  const { t } = useTranslation();
  const [activityId, setActivityId] = useState<number | null>(initialData?.activityId ?? null);
  const [activityError, setActivityError] = useState<string | undefined>(undefined);

  const initialValues = useMemo(
    () => ({
      name: initialData?.name ?? '',
      description: initialData?.description ?? '',
    }),
    [initialData]
  );

  // Inline (non-entity) ActivityPicker rendered as a SchemaForm section item.
  const activityField = (
    <ActivityPicker
      key="activity-picker"
      value={activityId}
      onChange={(v) => {
        setActivityId(v);
        if (v !== null) setActivityError(undefined);
      }}
      required={!!(allowedActivityIds && allowedActivityIds.length > 0)}
      allowedActivityIds={allowedActivityIds}
      error={activityError}
      helpText={t('groups.activityHelp')}
    />
  );

  const handleSubmit = (values: Record<string, any>) => {
    // Mirror the legacy scoped-admin requirement: an activity must be chosen.
    if (allowedActivityIds && allowedActivityIds.length > 0 && activityId === null) {
      setActivityError(t('activity.picker.requiredError'));
      return;
    }
    setActivityError(undefined);
    return onSubmit({
      name: values.name,
      description: values.description ?? '',
      activityId,
    });
  };

  return (
    <SchemaForm
      entity="group"
      sections={[{ items: ['name', 'description', activityField] }]}
      initialValues={initialValues}
      seedKey={seedKey}
      onSubmit={handleSubmit}
      submitLabel={submitButtonText}
      loading={loading}
    />
  );
};

export default GroupForm;
